import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useState, useRef } from "react";
import { Box, Typography, ClickAwayListener, IconButton, CircularProgress } from "@mui/material";
import { Check } from "lucide-react";
import HoverTooltip from "../../../common_components/hoverOverlay/HoverTooltip";
// Ensure the URL is absolute. Without a scheme (e.g. "test2.com"), the browser
// treats it as a path relative to our own origin, so window.open would open our
// own app page instead of the publisher's site.
const toAbsoluteUrl = (url) => /^https?:\/\//i.test(url) ? url : `https://${url}`;
export const InlineUrlEdit = React.memo(({ publisherName, publisherUrl, onSave, saving, disabled, isDirty }) => {
    const [editing, setEditing] = useState(false);
    const [position, setPosition] = useState({ top: 0, left: 0 });
    const inputRef = useRef(null);
    const buttonRef = useRef(null);
    // Close popup on scroll
    React.useEffect(() => {
        if (!editing)
            return;
        const handleScroll = () => {
            setEditing(false);
        };
        // Listen to scroll on window and all parent elements
        window.addEventListener('scroll', handleScroll, true);
        return () => {
            window.removeEventListener('scroll', handleScroll, true);
        };
    }, [editing]);
    const handleEditClick = (e) => {
        e.stopPropagation();
        if (buttonRef.current) {
            const rect = buttonRef.current.getBoundingClientRect();
            setPosition({
                top: rect.bottom + 4,
                left: rect.left
            });
        }
        setEditing(true);
    };
    const handleSave = async () => {
        const val = inputRef.current?.value.trim() || "";
        await onSave(val || null);
        setEditing(false);
    };
    return (_jsxs(Box, { sx: { position: "relative" }, children: [_jsxs(Box, { ref: buttonRef, sx: {
                    display: "flex", alignItems: "center", gap: 0.5,
                    px: 0.8, py: 0.4, mx: -0.8, borderRadius: "6px",
                    transition: "background 0.2s",
                    "&:hover": { bgcolor: "#f9fafb" },
                    "&:hover .edit-icon": { opacity: 1 },
                }, children: [_jsx(HoverTooltip, { title: publisherUrl ? `${publisherUrl}` : publisherName, placement: "top", children: _jsx(Typography, { className: "all-Font-Sizes", onClick: (e) => {
                                if (publisherUrl) {
                                    e.stopPropagation();
                                    window.open(toAbsoluteUrl(publisherUrl), "_blank", "noopener,noreferrer");
                                }
                            }, sx: {
                                fontWeight: isDirty ? 700 : 400,
                                color: isDirty ? "#d97706" : "#101828",
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                maxWidth: 130,
                                cursor: publisherUrl ? "pointer" : "default",
                                "&:hover": { textDecoration: publisherUrl ? "underline" : "none", color: publisherUrl ? "#ff008e" : "inherit" }
                            }, children: publisherName }) }), isDirty && _jsx(Box, { sx: { width: 6, height: 6, borderRadius: "50%", bgcolor: "#f59e0b", ml: 0.5 } }), !disabled && (_jsx(HoverTooltip, { title: "Edit URL", placement: "top", children: _jsx("img", { src: "/assets/link.svg", alt: "edit", className: "edit-icon", onClick: handleEditClick, style: {
                                marginLeft: "8px",
                                width: 14,
                                height: 14,
                                opacity: 0.6,
                                transition: "all 0.2s",
                                cursor: "pointer"
                            } }) }))] }), editing && (_jsx(ClickAwayListener, { onClickAway: () => setEditing(false), mouseEvent: "onMouseDown", touchEvent: "onTouchStart", children: _jsxs(Box, { onClick: e => e.stopPropagation(), onMouseDown: e => e.stopPropagation(), sx: {
                        position: "fixed",
                        top: `${position.top}px`,
                        left: `${position.left}px`,
                        zIndex: 9999,
                        width: 280, bgcolor: "#fff", borderRadius: "8px",
                        boxShadow: "0 8px 30px rgba(0,0,0,0.15)", border: "1.5px solid #e5e7eb", p: 1.2,
                    }, children: [_jsx(Typography, { sx: { fontSize: 11, fontWeight: 600, color: "#667085", mb: 0.5 }, children: "Publisher URL" }), _jsxs("div", { style: { display: "flex", gap: "6px" }, children: [_jsx("input", { ref: inputRef, defaultValue: publisherUrl || "", autoFocus: true, onMouseDown: e => e.stopPropagation(), className: "all-Font-Sizes-12", style: { flex: 1, color: "#101828", border: "1.5px solid #d0d5dd", borderRadius: "6px", padding: "6px 8px", outline: "none", boxSizing: "border-box", backgroundColor: "#fff" }, placeholder: "https://...", onKeyDown: e => { if (e.key === "Enter")
                                        handleSave(); if (e.key === "Escape")
                                        setEditing(false); } }), _jsx(IconButton, { size: "small", onClick: handleSave, disabled: saving, sx: { bgcolor: "#ff008e", color: "#fff", "&:hover": { bgcolor: "#e60080" }, borderRadius: "6px" }, children: saving ? _jsx(CircularProgress, { size: 14, color: "inherit" }) : _jsx(Check, { size: 14 }) })] })] }) }))] }));
});
